// Partner-Karten für /community/stream (NT-STREAM-PARTNERKARTEN-01).
// Geteilt von der Dashboard-Tab-Variante (stream.ts) und der öffentlichen Gast-Variante
// (stream-public.ts), damit beide dieselbe Karte zeigen.

import { navigate } from '../../router.js';
import { awardBadgeHtml } from '../../utils/award-badges.js';
import {
  STREAM_EMPTY_FOOTER,
  STREAM_EMPTY_HEADLINE,
  getEmptyLine,
} from '../../data/stream-texts.js';

export interface PartnerCardData {
  displayName: string;
  twitchUsername: string;
  slug: string;
  isLive: boolean;
  tagline: string;
  level: number | null;
  wantedLevel: number | null;
  crimeRank: { key: string; label: string; colorVar: string | null } | null;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** "LVL x · FL y" — nur wenn ein verknüpftes Spielerkonto existiert. */
export function formatPartnerStats(card: Pick<PartnerCardData, 'level' | 'wantedLevel'>): string | null {
  if (card.level === null || card.wantedLevel === null) return null;
  return `LVL ${String(card.level)} · FL ${String(card.wantedLevel)}`;
}

/** Link auf den Twitch-Kanal; der Login ist bei Twitch auf [a-z0-9_] beschränkt, wird trotzdem encodiert. */
export function twitchChannelUrl(twitchUsername: string): string {
  return `https://www.twitch.tv/${encodeURIComponent(twitchUsername)}`;
}

function renderCard(card: PartnerCardData): string {
  const stats = formatPartnerStats(card);
  const badge =
    card.crimeRank?.colorVar
      ? awardBadgeHtml(
          { awardKey: 'CRIME_RANK', label: card.crimeRank.label, colorVar: card.crimeRank.colorVar },
          'partner-card-badge',
        )
      : '';
  const live = card.isLive
    ? `<a class="partner-card-live" href="${esc(twitchChannelUrl(card.twitchUsername))}" target="_blank" rel="noopener noreferrer">● LIVE</a>`
    : '';

  return `
    <div class="partner-card${card.isLive ? ' partner-card--live' : ''}" data-partner-slug="${esc(card.slug)}" role="link" tabindex="0">
      <div class="partner-card-top">
        <span class="partner-card-name">${esc(card.displayName.toUpperCase())}</span>
        ${live}
      </div>
      ${stats ? `<div class="partner-card-stats">${esc(stats)}</div>` : ''}
      ${badge ? `<div class="partner-card-badges">${badge}</div>` : ''}
      <div class="partner-card-tagline">„${esc(card.tagline)}“</div>
    </div>`;
}

/**
 * Leerzustand (nur wenn niemand live ist) plus alle Karten. Die Server-Sortierung
 * (live zuerst, dann alphabetisch) bleibt unverändert.
 */
export function renderPartnerSection(cards: PartnerCardData[]): string {
  const anyLive = cards.some((c) => c.isLive);
  const empty = anyLive
    ? ''
    : `
      <div class="stream-offline-msg">
        <div class="stream-offline-label">${esc(STREAM_EMPTY_HEADLINE)}</div>
        <div class="stream-offline-sub">${esc(getEmptyLine())}<br>${esc(STREAM_EMPTY_FOOTER)}</div>
      </div>`;

  const grid =
    cards.length > 0 ? `<div class="partner-cards">${cards.map(renderCard).join('')}</div>` : '';
  return `${empty}${grid}`;
}

/** Klick/Enter auf die Karte → /community/<slug>; der LIVE-Link öffnet Twitch und bleibt davon unberührt. */
export function bindPartnerCards(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>('[data-partner-slug]').forEach((el) => {
    const go = (): void => {
      const slug = el.dataset.partnerSlug;
      if (slug) navigate(`/community/${slug}`);
    };
    el.addEventListener('click', (e) => {
      if (e.target instanceof Element && e.target.closest('a')) return;
      go();
    });
    el.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      if (e.target instanceof Element && e.target.closest('a')) return;
      go();
    });
  });
}
